import React, { useMemo, useState, useEffect } from 'react';
import Modal from './Modal';
import './ClienteSelectorModal.css';

/**
 * Modal de selección de cliente con buscador.
 *
 * Reemplaza el `<select>` nativo cuando la lista es larga: en lugar de
 * desplegar todos los clientes en un dropdown del browser, abre un popup
 * con input de búsqueda que filtra por nombre, apellido o email.
 *
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {Array}    props.clientes        Lista completa de clientes.
 * @param {string=}  props.selectedClienteId   Para resaltar el actual.
 * @param {Function} props.onSelect        (cliente) => void
 * @param {string=}  props.title           Default: "Seleccionar cliente"
 */
const ClienteSelectorModal = ({
  isOpen,
  onClose,
  clientes = [],
  selectedClienteId,
  onSelect,
  title = 'Seleccionar cliente',
}) => {
  const [busqueda, setBusqueda] = useState('');

  // Reset búsqueda cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) setBusqueda('');
  }, [isOpen]);

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return clientes;
    const q = busqueda.toLowerCase();
    return clientes.filter(
      (c) =>
        c.nombre?.toLowerCase().includes(q) ||
        c.apellido?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
    );
  }, [clientes, busqueda]);

  const handleSelect = (cliente) => {
    onSelect(cliente);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <div className="cliente-selector">
        <input
          type="text"
          className="input cliente-selector__search"
          placeholder="🔍 Buscar por nombre, apellido o email..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          autoFocus
        />

        <div className="cliente-selector__count">
          {filtrados.length} de {clientes.length}
        </div>

        <ul className="cliente-selector__list">
          {filtrados.length === 0 ? (
            <li className="cliente-selector__empty">No hay clientes que coincidan.</li>
          ) : (
            filtrados.map((cliente) => {
              const isSelected = String(cliente._id) === String(selectedClienteId);
              return (
                <li key={cliente._id}>
                  <button
                    type="button"
                    className={`cliente-selector__item ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => handleSelect(cliente)}
                  >
                    <span className="cliente-selector__name">
                      {cliente.apellido} {cliente.nombre}
                    </span>
                    <span className="cliente-selector__email">{cliente.email}</span>
                    {cliente.activo === false && (
                      <span className="cliente-selector__badge">inactivo</span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </Modal>
  );
};

export default ClienteSelectorModal;
